/**
 * Metadata Service
 *
 * Purpose: Handles all API communications for metadata management including
 *          field definitions, fields, models, views, and tenant context management
 *
 * Architecture: Service layer that interfaces with the metadata module backend
 *              API endpoints, providing typed methods for all CRUD operations
 *
 * Dependencies: ApiService, Metadata types
 * Author: Development Team
 * Created: 2025-01-27
 */

import { apiService } from './api';
import { ApiResponse } from '../types/auth';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

export interface FieldDefinition {
  fielddef_id: string;
  tenant_id: string;
  label: string;
  description?: string | null;
  datatype: 'list' | 'text' | 'bool' | 'currency' | 'decimal' | 'percent' | 'date' | 'file';
  is_multiselect: boolean;
  creation_access?: 'User' | 'Admin' | 'None';
  storage_decimal_places?: number | null;
  display_decimal_places?: number | null;
  status: 'active' | 'disabled' | 'deleted';
  access: '0' | '1' | '2' | '3'; // Internal, Read-Only, Overrideable, Overrideable All
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  created_by_user?: {
    email: string;
    user_id: string;
    first_name?: string;
    last_name?: string;
  };
  updated_by_user?: {
    email: string;
    user_id: string;
    first_name?: string;
    last_name?: string;
  };
  scope_info?: {
    scope: 'GLOBAL' | 'Overridden' | 'LOCAL';
    is_override: boolean;
    has_global_parent: boolean;
  };
  related_fields_count?: number;
  hierarchy_config?: HierarchyConfig | null;
  // Source field definition configuration
  sourcevalue_fielddef_id?: string | null;
  sourcevalue_filter?: SourceFieldFilter | null;
  source_field_definition?: SourceFieldInfo | null;
  // File field configuration
  file_config?: FileFieldConfig | null;
}

export interface FileFieldConfig {
  allowed_extensions: string[];
  max_size_mb: number;
  max_files: number;
  generate_thumbnails: boolean;
  allowed_mime_types?: string[] | null;
}

export interface HierarchyConfig {
  hierarchy_label: string;
  hierarchy_level: number;
  parent_fielddef_id?: string | null;
  child_fielddef_id?: string | null;
}

// Source field filter structure
export interface SourceFieldFilter {
  operator: 'AND' | 'OR';
  conditions: FilterCondition[];
}

// Individual filter condition
export interface FilterCondition {
  related_fielddef_id: string;
  operator: FilterOperator;
  value?: any; // Optional for IS NULL / IS NOT NULL
}

// Filter operators
export type FilterOperator =
  | 'IN'
  | '='
  | '!='
  | '>'
  | '>='
  | '<'
  | '<='
  | 'IS NULL'
  | 'IS NOT NULL';

// Resolved source field info
export interface SourceFieldInfo {
  fielddef_id: string;
  tenant_id: string;
  label: string;
}

// Related field for filter builder
export interface RelatedFieldDefinition {
  fielddef_id: string;
  label: string;
  datatype: string;
}

export interface FieldDefinitionFormData {
  fielddef_id: string;
  tenant_id: string;
  label: string;
  description?: string | null;
  datatype: FieldDefinition['datatype'];
  is_multiselect: boolean;
  creation_access?: 'User' | 'Admin' | 'None';
  storage_decimal_places?: number | null;
  display_decimal_places?: number | null;
  access: FieldDefinition['access'];
  hierarchy_config?: HierarchyConfig | null;
  // Source field configuration
  sourcevalue_fielddef_id?: string | null;
  sourcevalue_filter?: SourceFieldFilter | null;
  // File field configuration
  file_config?: FileFieldConfig | null;
}

export interface FieldDefinitionUpdateData {
  label?: string;
  description?: string | null;
  datatype?: FieldDefinition['datatype'];
  is_multiselect?: boolean;
  creation_access?: 'User' | 'Admin' | 'None';
  storage_decimal_places?: number | null;
  display_decimal_places?: number | null;
  access?: FieldDefinition['access'];
  status?: FieldDefinition['status'];
}

export interface Field {
  field_id: string;
  fielddef_id: string;
  tenant_id: string;
  model_id: string;
  model_name?: string;
  model_type?: 'normal' | 'fielddef';
  storage_type: 'fixed' | 'metadata';
  label_override?: string;
  description_override?: string | null;
  field_definition_label?: string;
  is_required?: boolean;
  is_multiselect?: boolean;
  /** ORCH-418: multi-select list fields offer "All" (stores ALL). Default true */
  allow_select_all?: boolean;
  /** ORCH-400: related/subset picker filtering settings; null = every source, on */
  filter_config?: FieldFilterConfig | null;
  storage_decimal_places_override?: number | null;
  display_decimal_places_override?: number | null;
  status_override?: 'active' | 'disabled' | 'deleted';
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  datatype?: string; // For view field responses that include datatype directly
  created_by_user?: {
    email: string;
    user_id: string;
    first_name?: string;
    last_name?: string;
  };
  updated_by_user?: {
    email: string;
    user_id: string;
    first_name?: string;
    last_name?: string;
  };
  scope_info?: {
    scope: 'GLOBAL' | 'Overridden' | 'LOCAL';
    is_override: boolean;
    has_global_parent: boolean;
  };
  field_definition?: {
    datatype: FieldDefinition['datatype'];
    label: string;
  };
}

export interface FieldFormData {
  fielddef_id: string;
  tenant_id: string;
  model_id: string;
  storage_type: Field['storage_type'];
  label_override?: string;
  description_override?: string | null;
  is_required?: boolean;
  is_multiselect?: boolean;
  allow_select_all?: boolean;
  filter_config?: FieldFilterConfig | null;
  storage_decimal_places_override?: number | null;
  display_decimal_places_override?: number | null;
  status_override?: Field['status_override'];
}

/**
 * ORCH-400: which candidate sources narrow a list Field's picker and save check.
 * Stored as exclusions so a new related field filters by default.
 */
export interface FieldFilterConfig {
  /** false turns every related/subset source off (security scope is never governed here) */
  enabled?: boolean;
  exclude?: Array<{ model_id: string; fielddef_id: string }>;
}

/** One field that narrows a picker (ORCH-400 §4.4) */
export interface PickerFilterSource {
  model_id: string;
  model_label?: string;
  fielddef_id: string;
  field_label?: string;
  kind: 'related' | 'subset';
  excluded: boolean;
}

/** The candidate sources of one list Field */
export interface PickerFilter {
  fielddef_id: string;
  field_label?: string;
  enabled: boolean;
  sources: PickerFilterSource[];
}

export interface PickerFilterGraph {
  model_id: string;
  fields: PickerFilter[];
}

/** One field's failure from the picker-filters validate endpoint (ORCH-400 §4.9) */
export interface PickerFilterFailure {
  ids: string[];
  reason: 'security' | 'security_removed' | 'enabled_list' | 'related' | 'subset';
  reasons: Record<string, string>;
  sources: string[];
  message: string;
}

export interface PickerFilterCheck {
  fields: Record<string, PickerFilterFailure>;
  /** fielddef_id -> stored ids outside the caller's access that the record keeps */
  locked: Record<string, string[]>;
  children: Record<string, { count: number; child_model_id: string; ids: string[] }>;
}

// Field-View junction types for managing field display in views (with multi-tenant override support)
export interface ScopeInfo {
  scope: 'GLOBAL' | 'OVERRIDDEN' | 'LOCAL';
  is_override: boolean;
  parent_tenant_id?: string | null;
}

export interface FieldView {
  id: string;
  view_id: string;
  view_tenant_id: string;
  fielddef_id: string;
  model_id: string;
  tenant_id: string; // This FieldView's tenant (for override capability)
  display_order: number;
  is_visible: boolean;
  is_sortable: boolean;
  is_filterable: boolean;
  is_groupable: boolean;
  is_resizable: boolean;
  header_label_override?: string | null;
  display_subcontainer?: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  // Scope information for UI
  scope_info?: ScopeInfo;
  // Resolved field info
  field_label?: string;
  field_datatype?: string;
}

export interface FieldViewCreate {
  fielddef_id: string;
  model_id: string;
  tenant_id?: string; // Defaults to 'GLOBAL', can be tenant-specific override
  display_order?: number;
  is_visible?: boolean;
  is_sortable?: boolean;
  is_filterable?: boolean;
  is_groupable?: boolean;
  is_resizable?: boolean;
  header_label_override?: string | null;
  display_subcontainer?: string | null;
}

export interface FieldViewUpdate {
  display_order?: number;
  is_visible?: boolean;
  is_sortable?: boolean;
  is_filterable?: boolean;
  is_groupable?: boolean;
  is_resizable?: boolean;
  header_label_override?: string | null;
  display_subcontainer?: string | null;
}

export interface FieldViewBatchCreate {
  fields: Array<{ fielddef_id: string; model_id: string }>;
  tenant_id?: string;
  is_visible?: boolean;
  is_sortable?: boolean;
  is_filterable?: boolean;
  is_groupable?: boolean;
  is_resizable?: boolean;
}

export interface FieldViewReorder {
  field_orders: Array<{ fielddef_id: string; model_id: string; display_order: number }>;
  tenant_id?: string;
}

export interface Model {
  model_id: string;
  name: string;
  model_type?: 'normal' | 'fielddef';
  fielddef_id?: string;
  fielddef_tenant_id?: string;
  /** ORCH-400: the model whose record holds this model's records (flight -> mediaplan) */
  parent_model_id?: string | null;
}

export interface ModelFormData {
  model_id: string;
  name: string;
}

export interface View {
  view_id: string;
  tenant_id: string; // Part of composite key (view_id, tenant_id)
  model_id: string;
  model_name?: string;
  model_type?: 'normal' | 'fielddef';
  view_type: 'table' | 'edit-modal' | 'export' | 'default';
  label: string;
  description?: string | null;
  status: string;
  field_count: number;
  fields?: ViewField[];
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
  created_by_user?: {
    email: string;
    user_id: string;
    first_name?: string;
    last_name?: string;
  };
  updated_by_user?: {
    email: string;
    user_id: string;
    first_name?: string;
    last_name?: string;
  };
  // Scope information for UI (GLOBAL, OVERRIDDEN, or LOCAL)
  scope_info?: ScopeInfo;
}

export interface ViewField {
  // Logical field reference (new structure)
  fielddef_id: string;
  model_id: string;
  tenant_id: string; // FieldView's tenant
  view_id: string;
  view_tenant_id: string;
  // Display configuration from field_views junction table
  field_view_id?: string;
  display_order: number;
  is_visible?: boolean;
  is_sortable?: boolean;
  is_filterable?: boolean;
  is_groupable?: boolean;
  is_resizable?: boolean;
  header_label_override?: string | null;
  display_subcontainer?: string | null;
  // Scope information for UI
  scope_info?: ScopeInfo;
  // Resolved field info from field definition
  field_label?: string;
  field_datatype?: string;
  // Legacy compatibility alias
  view_position?: number;
  // Legacy fields (for backward compatibility during transition)
  field_id?: string;
  label?: string;
  datatype?: string;
  storage_type?: 'fixed' | 'metadata';
  label_override?: string;
  is_required?: boolean;
  is_multiselect?: boolean;
  storage_decimal_places?: number;
  display_decimal_places?: number;
  field_definition?: {
    fielddef_id: string;
    label: string;
    datatype: string;
    is_multiselect: boolean;
    storage_decimal_places?: number | null;
    display_decimal_places?: number | null;
    hierarchy_config?: Record<string, any>;
  };
}

export interface ViewFormData {
  view_id: string;
  model_id: string;
  view_type: 'table' | 'edit-modal' | 'export';
  label: string;
  description?: string | null;
  tenant_id?: string;
  status?: string; // Optional status for updates
  fields?: FieldViewCreate[]; // Array of field assignments with display properties
}

export interface TenantInfo {
  tenant_id: string;
  name: string;
  is_global: boolean;
  can_edit: boolean;
}

export interface MetadataManagerContext {
  current_tenant: TenantInfo;
  available_tenants: TenantInfo[];
  user_permissions: {
    can_edit_global: boolean;
    can_edit_tenant: boolean;
    can_switch_context: boolean;
  };
}

export interface Schema {
  model_id: string;
  tenant_id: string;
  fields: Array<{
    field_id: string;
    fielddef_id: string;
    label: string;
    datatype: string;
    is_required: boolean;
    is_multiselect: boolean;
  }>;
  available_views: Array<{
    view_id: string;
    label: string;
    field_count: number;
  }>;
}

export interface FormConfig {
  model_id: string;
  tenant_id: string;
  form_config: {
    fields: Array<{
      field_id: string;
      label: string;
      type: string;
      required: boolean;
      multiple: boolean;
      options?: string[];
    }>;
    layout: string;
    validation_rules: Record<string, any>;
  };
}

// Query parameters for list endpoints
export interface FieldDefinitionQueryParams {
  tenant_id?: string;
  status?: string;
  datatype?: string;
}

export interface FieldQueryParams {
  tenant_id: string;
  model_id?: string;
  status?: string;
}

// =============================================================================
// METADATA SERVICE CLASS
// =============================================================================

/**
 * Metadata Service Class
 *
 * Provides comprehensive metadata management functionality including:
 * - Field definition CRUD operations
 * - Field instance CRUD operations
 * - Model and view management
 * - Schema generation and form configuration
 * - Tenant context management
 */
class MetadataService {
  private readonly baseUrl = '/metadata';

  /** In-memory cache for getFieldDefinition() to avoid redundant /field-definitions fetches */
  private fieldDefCache = new Map<string, { data: FieldDefinition[]; timestamp: number }>();
  private readonly FIELD_DEF_CACHE_TTL = 60_000; // 60 seconds

  // =============================================================================
  // METADATA MANAGER CONTEXT
  // =============================================================================

  /**
   * Get current metadata management context
   */
  async getManagerContext(
    tenantId?: string,
    signal?: AbortSignal
  ): Promise<ApiResponse<MetadataManagerContext>> {
    const params = tenantId ? { tenant_id: tenantId } : {};
    const response = await apiService.api.get<ApiResponse<MetadataManagerContext>>(
      `${this.baseUrl}/manager/context`,
      {
        params,
        signal,
      }
    );
    return response.data;
  }

  /**
   * Switch metadata management context to different tenant
   */
  async switchContext(tenantId: string): Promise<ApiResponse<{ status: string }>> {
    const response = await apiService.api.post<ApiResponse<{ status: string }>>(
      `${this.baseUrl}/manager/switch/${tenantId}`
    );
    return response.data;
  }

  // =============================================================================
  // FIELD DEFINITIONS
  // =============================================================================

  /**
   * Get all field definitions
   */
  async getHierarchyLabels(tenantId?: string): Promise<string[]> {
    /**
     * Get all unique hierarchy labels from field definitions
     *
     * Purpose: Retrieve existing hierarchy labels for autocomplete suggestions
     * Input: Optional tenantId to filter by tenant
     * Output: List of unique hierarchy labels (sorted alphabetically)
     */
    const params = tenantId ? { tenant_id: tenantId } : {};
    const response = await apiService.api.get<string[]>(
      `${this.baseUrl}/field-definitions/hierarchy-labels`,
      { params }
    );
    return response.data || [];
  }

  async getFieldDefinitions(params: FieldDefinitionQueryParams = {}): Promise<FieldDefinition[]> {
    const response = await apiService.api.get<FieldDefinition[]>(
      `${this.baseUrl}/field-definitions`,
      { params }
    );
    return response.data;
  }

  /**
   * Get single field definition
   */
  async getFieldDefinition(fielddefId: string, tenantId: string): Promise<FieldDefinition> {
    const cacheKey = tenantId;
    const cached = this.fieldDefCache.get(cacheKey);
    let allDefs: FieldDefinition[];

    if (cached && Date.now() - cached.timestamp < this.FIELD_DEF_CACHE_TTL) {
      allDefs = cached.data;
    } else {
      const response = await apiService.api.get<FieldDefinition[]>(
        `${this.baseUrl}/field-definitions`,
        { params: { tenant_id: tenantId } }
      );
      allDefs = response.data;
      this.fieldDefCache.set(cacheKey, { data: allDefs, timestamp: Date.now() });
    }

    const fieldDef = allDefs.find((fd) => fd.fielddef_id === fielddefId);
    if (!fieldDef) {
      throw new Error(`Field definition ${fielddefId} not found for tenant ${tenantId}`);
    }

    return fieldDef;
  }

  /**
   * Create new field definition
   */
  async createFieldDefinition(
    data: FieldDefinitionFormData
  ): Promise<ApiResponse<{ fielddef_id: string; status: string }>> {
    const response = await apiService.api.post<
      ApiResponse<{ fielddef_id: string; status: string }>
    >(`${this.baseUrl}/field-definitions`, data);
    return response.data;
  }

  /**
   * Update existing field definition
   */
  async updateFieldDefinition(
    fielddefId: string,
    tenantId: string,
    data: FieldDefinitionUpdateData,
    currentTenantId?: string
  ): Promise<ApiResponse<{ fielddef_id: string; tenant_id: string; status: string }>> {
    const params = currentTenantId ? { current_tenant_id: currentTenantId } : {};
    const response = await apiService.api.put<
      ApiResponse<{ fielddef_id: string; tenant_id: string; status: string }>
    >(`${this.baseUrl}/field-definitions/${fielddefId}/${tenantId}`, data, { params });
    return response.data;
  }

  /**
   * Delete field definition (soft delete)
   */
  async deleteFieldDefinition(
    fielddefId: string,
    tenantId: string
  ): Promise<ApiResponse<{ fielddef_id: string; tenant_id: string; status: string }>> {
    const response = await apiService.api.delete<
      ApiResponse<{ fielddef_id: string; tenant_id: string; status: string }>
    >(`${this.baseUrl}/field-definitions/${fielddefId}/${tenantId}`);
    return response.data;
  }

  // =============================================================================
  // FIELDS
  // =============================================================================

  /**
   * Get all fields
   */
  async getFields(params: FieldQueryParams): Promise<Field[]> {
    const response = await apiService.api.get<Field[]>(`${this.baseUrl}/fields`, { params });
    return response.data;
  }

  /**
   * Get single field
   */
  async getField(fieldId: string): Promise<ApiResponse<Field>> {
    const response = await apiService.api.get<ApiResponse<Field>>(
      `${this.baseUrl}/fields/${fieldId}`
    );
    return response.data;
  }

  /**
   * Create new field instance
   */
  async createField(
    data: FieldFormData
  ): Promise<ApiResponse<{ field_id: string; status: string }>> {
    const response = await apiService.api.post<ApiResponse<{ field_id: string; status: string }>>(
      `${this.baseUrl}/fields`,
      data
    );
    return response.data;
  }

  /**
   * Update existing field instance
   */
  async updateField(
    fieldId: string,
    data: FieldFormData,
    currentTenantId?: string
  ): Promise<ApiResponse<{ field_id: string; status: string }>> {
    const params = currentTenantId ? { current_tenant_id: currentTenantId } : {};
    const response = await apiService.api.put<ApiResponse<{ field_id: string; status: string }>>(
      `${this.baseUrl}/fields/${fieldId}`,
      data,
      { params }
    );
    return response.data;
  }

  /**
   * Which fields narrow each list picker on a model (ORCH-400 §4.4)
   *
   * Output: every candidate source, excluded ones included; forms keep the enabled,
   *         non-excluded ones and the Metadata Manager shows them all
   */
  async getPickerFilters(modelId: string): Promise<PickerFilterGraph> {
    const response = await apiService.api.get<PickerFilterGraph>(
      `${this.baseUrl}/models/${modelId}/picker-filters`
    );
    return response.data;
  }

  /**
   * Which of a form's selected list values fail their filters (ORCH-400 §4.9)
   *
   * Logic: the same check the save path runs, so the form's field errors and the API's
   *        400 can't disagree. Advisory; writes nothing.
   * Input: values - fielddef_id -> value; permission - the write the form will make;
   *        recordId - the record an edit form opened (its stored values decide what is
   *        locked); ancestors - model_id -> that ancestor record's values
   */
  async validatePickerFilters(
    modelId: string,
    body: {
      values: Record<string, unknown>;
      permission?: string | null;
      record_id?: string | null;
      ancestors?: Record<string, Record<string, unknown>>;
    }
  ): Promise<PickerFilterCheck> {
    const response = await apiService.api.post<PickerFilterCheck>(
      `${this.baseUrl}/models/${modelId}/picker-filters/validate`,
      body
    );
    return response.data;
  }

  /**
   * Delete field instance (soft delete)
   */
  async deleteField(fieldId: string): Promise<ApiResponse<{ field_id: string; status: string }>> {
    const response = await apiService.api.delete<ApiResponse<{ field_id: string; status: string }>>(
      `${this.baseUrl}/fields/${fieldId}`
    );
    return response.data;
  }

  // =============================================================================
  // MODELS
  // =============================================================================

  /**
   * Get all models
   */
  async getModels(): Promise<Model[]> {
    const response = await apiService.api.get<Model[]>(`${this.baseUrl}/models`);
    return response.data;
  }

  /**
   * Get single model
   */
  async getModel(modelId: string): Promise<ApiResponse<Model>> {
    const response = await apiService.api.get<ApiResponse<Model>>(
      `${this.baseUrl}/models/${modelId}`
    );
    return response.data;
  }

  /**
   * Get models filtered by type
   */
  async getModelsByType(modelType?: 'normal' | 'fielddef'): Promise<Model[]> {
    const params = modelType ? `?model_type=${modelType}` : '';
    const response = await apiService.api.get<Model[]>(`${this.baseUrl}/models/by-type${params}`);
    return response.data;
  }

  /**
   * Get FieldDefinition models only
   */
  async getFieldDefinitionModels(): Promise<Model[]> {
    const response = await apiService.api.get<Model[]>(`${this.baseUrl}/models/fielddef`);
    return response.data;
  }

  /**
   * Get normal business models only
   */
  async getNormalModels(): Promise<Model[]> {
    const response = await apiService.api.get<Model[]>(`${this.baseUrl}/models/normal`);
    return response.data;
  }

  /**
   * Get model for a specific FieldDefinition
   */
  async getModelForFieldDefinition(fielddefId: string, tenantId: string): Promise<Model> {
    const response = await apiService.api.get<Model>(
      `${this.baseUrl}/field-definitions/${fielddefId}/${tenantId}/model`
    );
    return response.data;
  }

  /**
   * Get related fields for a FieldDefinition (GLOBAL + tenant-specific)
   */
  async getFieldDefinitionFields(fielddefId: string, tenantId: string): Promise<Field[]> {
    const response = await apiService.api.get<Field[]>(
      `${this.baseUrl}/field-definitions/${fielddefId}/${tenantId}/fields`
    );
    return response.data;
  }

  /**
   * Get related fields for a field definition (for filter builder)
   * Used to populate filter builder dropdowns
   */
  async getRelatedFields(fielddefId: string, tenantId: string): Promise<RelatedFieldDefinition[]> {
    const response = await apiService.api.get<Field[]>(
      `${this.baseUrl}/field-definitions/${fielddefId}/${tenantId}/fields`
    );

    // Extract unique field definitions from fields
    const relatedFieldsMap = new Map<string, RelatedFieldDefinition>();

    response.data.forEach((field: any) => {
      if (!relatedFieldsMap.has(field.fielddef_id)) {
        relatedFieldsMap.set(field.fielddef_id, {
          fielddef_id: field.fielddef_id,
          label: field.field_definition_label || field.label_override || field.fielddef_id,
          datatype: field.datatype || field.field_definition?.datatype || 'text',
        });
      }
    });

    return Array.from(relatedFieldsMap.values());
  }

  /**
   * Validate source filter configuration
   * Returns validation result or throws error
   */
  async validateSourceFilter(
    sourceFielddefId: string,
    sourceFilter: SourceFieldFilter,
    tenantId: string
  ): Promise<{ is_valid: boolean }> {
    const response = await apiService.api.post<{ is_valid: boolean }>(
      `${this.baseUrl}/field-definitions/validate-source-filter`,
      sourceFilter,
      {
        params: {
          source_fielddef_id: sourceFielddefId,
          tenant_id: tenantId,
        },
      }
    );

    return response.data;
  }

  /**
   * Get list-type field definitions for source selector
   * Excludes the current field to prevent self-reference
   */
  async getListFieldDefinitions(
    tenantId: string,
    excludeFielddefId?: string
  ): Promise<FieldDefinition[]> {
    const allFieldDefs = await this.getFieldDefinitions({
      tenant_id: tenantId,
      datatype: 'list',
      status: 'active',
    });

    // Exclude current field definition and source field definitions
    return allFieldDefs.filter((fd) => {
      if (excludeFielddefId && fd.fielddef_id === excludeFielddefId) {
        return false;
      }
      // Don't allow selecting a field that itself has a source
      // (prevents chaining: A -> B -> C)
      if (fd.sourcevalue_fielddef_id) {
        return false;
      }
      return true;
    });
  }

  /**
   * Create new model
   */
  async createModel(
    data: ModelFormData
  ): Promise<ApiResponse<{ model_id: string; status: string }>> {
    const response = await apiService.api.post<ApiResponse<{ model_id: string; status: string }>>(
      `${this.baseUrl}/models`,
      data
    );
    return response.data;
  }

  /**
   * Update existing model
   */
  async updateModel(
    modelId: string,
    data: ModelFormData
  ): Promise<ApiResponse<{ model_id: string; status: string }>> {
    const response = await apiService.api.put<ApiResponse<{ model_id: string; status: string }>>(
      `${this.baseUrl}/models/${modelId}`,
      data
    );
    return response.data;
  }

  /**
   * Delete model
   */
  async deleteModel(modelId: string): Promise<ApiResponse<{ model_id: string; status: string }>> {
    const response = await apiService.api.delete<ApiResponse<{ model_id: string; status: string }>>(
      `${this.baseUrl}/models/${modelId}`
    );
    return response.data;
  }

  // =============================================================================
  // VIEWS
  // =============================================================================

  /**
   * Get all views
   * @param tenantId - Optional tenant filter
   */
  async getViews(tenantId?: string): Promise<View[]> {
    try {
      const params = tenantId ? { tenant_id: tenantId } : {};
      const response = await apiService.api.get<View[]>(`${this.baseUrl}/views`, { params });
      return response.data;
    } catch (error) {
      // Endpoint not implemented yet, return empty array
      return [];
    }
  }

  /**
   * Get single view by view_id and tenant_id
   *
   * Purpose: Fetch view configuration by composite key (view_id, tenant_id)
   * Logic: Calls /views/{viewId} endpoint with tenant_id query param
   * Input: viewId, tenantId (defaults to 'GLOBAL')
   * Output: View configuration with metadata and scope info
   */
  async getView(viewId: string, tenantId: string = 'GLOBAL'): Promise<ApiResponse<View>> {
    const response = await apiService.api.get<ApiResponse<View>>(
      `${this.baseUrl}/views/${viewId}`,
      { params: { tenant_id: tenantId } }
    );
    return response.data;
  }

  /**
   * Get view configuration for a model (UNIVERSAL VIEW RETRIEVAL)
   *
   * Purpose: Fetches view configuration for any model type with related fields
   * Architecture: Uses model-focused metadata endpoint for universal view retrieval
   * Logic: Calls /models/{model_id}/view endpoint with view_type and tenant_id parameters
   * Input:
   *   - modelId: Model identifier (e.g., 'campaign', 'vendor', or fielddef_id like 'fd_fd_vendor')
   *   - tenantId: Tenant context for the view
   *   - viewType: Type of view ('edit-modal', 'table', 'list', 'detail')
   * Output: Promise resolving to ViewConfiguration with ordered related fields
   * Exceptions: Throws error if view configuration fetch fails
   *
   * Note: This is the recommended method for retrieving views across the application.
   *       For field definition models, modelId is the fielddef_id (e.g., 'fd_fd_vendor').
   *       For other models, modelId is the model identifier (e.g., 'campaign', 'vendor').
   *
   * Examples:
   *   - Campaign model: getModelView('campaign', 'tenant_123', 'edit-modal')
   *   - Field Definition: getModelView('fd_fd_vendor', 'GLOBAL', 'table')
   *
   * @param modelId - Model identifier
   * @param tenantId - Tenant ID for view context
   * @param viewType - View type: 'edit-modal', 'table', 'list', or 'detail'
   * @returns Promise resolving to view configuration
   */
  async getModelView(
    modelId: string,
    tenantId: string,
    viewType: 'edit-modal' | 'table' | 'list' | 'detail'
  ): Promise<any> {
    const response = await apiService.api.get(`${this.baseUrl}/models/${modelId}/view`, {
      params: {
        view_type: viewType,
        tenant_id: tenantId,
      },
    });
    return response.data;
  }

  /**
   * Get view fields with tenant override merging
   * @param viewId - View identifier
   * @param viewTenantId - View's tenant (defaults to 'GLOBAL')
   * @param currentTenantId - Current tenant context for merging (defaults to 'GLOBAL')
   * @param includeHidden - Include hidden fields (defaults to false)
   */
  async getViewFields(
    viewId: string,
    viewTenantId: string = 'GLOBAL',
    currentTenantId: string = 'GLOBAL',
    includeHidden: boolean = false
  ): Promise<ViewField[]> {
    const response = await apiService.api.get<ViewField[]>(
      `${this.baseUrl}/views/${viewId}/fields`,
      {
        params: {
          view_tenant_id: viewTenantId,
          current_tenant_id: currentTenantId,
          include_hidden: includeHidden,
        },
      }
    );
    return response.data;
  }

  /**
   * Create new view
   * @param data - View form data
   * @param currentTenantId - Current tenant context (optional)
   */
  async createView(
    data: ViewFormData,
    currentTenantId?: string
  ): Promise<ApiResponse<{ view_id: string; status: string }>> {
    const params = currentTenantId ? { current_tenant_id: currentTenantId } : {};
    const response = await apiService.api.post<ApiResponse<{ view_id: string; status: string }>>(
      `${this.baseUrl}/views`,
      data,
      { params }
    );
    return response.data;
  }

  /**
   * Update existing view (with override support)
   * @param viewId - View identifier
   * @param data - View form data
   * @param tenantId - View's tenant ID (defaults to 'GLOBAL')
   * @param currentTenantId - Current tenant context for override creation (optional)
   */
  async updateView(
    viewId: string,
    data: ViewFormData,
    tenantId: string = 'GLOBAL',
    currentTenantId?: string
  ): Promise<ApiResponse<{ view_id: string; status: string }>> {
    const params: Record<string, string> = { tenant_id: tenantId };
    if (currentTenantId) {
      params.current_tenant_id = currentTenantId;
    }
    const response = await apiService.api.put<ApiResponse<{ view_id: string; status: string }>>(
      `${this.baseUrl}/views/${viewId}`,
      data,
      { params }
    );
    return response.data;
  }

  /**
   * Delete view
   */
  async deleteView(
    viewId: string,
    tenantId: string = 'GLOBAL'
  ): Promise<ApiResponse<{ view_id: string; status: string }>> {
    const response = await apiService.api.delete<ApiResponse<{ view_id: string; status: string }>>(
      `${this.baseUrl}/views/${viewId}`,
      { params: { tenant_id: tenantId } }
    );
    return response.data;
  }

  // =============================================================================
  // FIELD-VIEW OPERATIONS (Junction Table with Multi-Tenant Override Support)
  // =============================================================================

  /**
   * Add a field to a view with display configuration
   * @param viewId - View identifier
   * @param fieldData - Field view create data (fielddef_id, model_id, display properties)
   * @param viewTenantId - View's tenant ID (defaults to 'GLOBAL')
   * @param currentTenantId - Current tenant context (optional)
   */
  async addFieldToView(
    viewId: string,
    fieldData: FieldViewCreate,
    viewTenantId: string = 'GLOBAL',
    currentTenantId?: string
  ): Promise<FieldView> {
    const params: Record<string, string> = { view_tenant_id: viewTenantId };
    if (currentTenantId) {
      params.current_tenant_id = currentTenantId;
    }
    const response = await apiService.api.post<FieldView>(
      `${this.baseUrl}/views/${viewId}/fields`,
      fieldData,
      { params }
    );
    return response.data;
  }

  /**
   * Update a field's display properties in a view (with override support)
   * @param viewId - View identifier
   * @param fielddefId - Field definition ID
   * @param modelId - Model ID
   * @param updateData - Update data
   * @param viewTenantId - View's tenant ID (defaults to 'GLOBAL')
   * @param currentTenantId - Current tenant context for override creation (optional)
   */
  async updateFieldView(
    viewId: string,
    fielddefId: string,
    modelId: string,
    updateData: FieldViewUpdate,
    viewTenantId: string = 'GLOBAL',
    currentTenantId?: string
  ): Promise<FieldView> {
    const params: Record<string, string> = {
      view_tenant_id: viewTenantId,
      model_id: modelId,
    };
    if (currentTenantId) {
      params.current_tenant_id = currentTenantId;
    }
    const response = await apiService.api.put<FieldView>(
      `${this.baseUrl}/views/${viewId}/fields/${fielddefId}`,
      updateData,
      { params }
    );
    return response.data;
  }

  /**
   * Remove a field from a view
   * @param viewId - View identifier
   * @param fielddefId - Field definition ID
   * @param modelId - Model ID
   * @param viewTenantId - View's tenant ID (defaults to 'GLOBAL')
   * @param tenantId - FieldView tenant ID to delete (defaults to 'GLOBAL')
   */
  async removeFieldFromView(
    viewId: string,
    fielddefId: string,
    modelId: string,
    viewTenantId: string = 'GLOBAL',
    tenantId: string = 'GLOBAL'
  ): Promise<ApiResponse<{ status: string; message: string }>> {
    const response = await apiService.api.delete<ApiResponse<{ status: string; message: string }>>(
      `${this.baseUrl}/views/${viewId}/fields/${fielddefId}`,
      {
        params: {
          view_tenant_id: viewTenantId,
          model_id: modelId,
          tenant_id: tenantId,
        },
      }
    );
    return response.data;
  }

  /**
   * Add multiple fields to a view in batch
   * @param viewId - View identifier
   * @param batchData - Batch create data with fields array
   * @param viewTenantId - View's tenant ID (defaults to 'GLOBAL')
   */
  async addFieldsBatch(
    viewId: string,
    batchData: FieldViewBatchCreate,
    viewTenantId: string = 'GLOBAL'
  ): Promise<FieldView[]> {
    const response = await apiService.api.post<FieldView[]>(
      `${this.baseUrl}/views/${viewId}/fields/batch`,
      batchData,
      { params: { view_tenant_id: viewTenantId } }
    );
    return response.data;
  }

  /**
   * Reorder fields in a view
   * @param viewId - View identifier
   * @param reorderData - Reorder data with field_orders array
   * @param viewTenantId - View's tenant ID (defaults to 'GLOBAL')
   */
  async reorderViewFields(
    viewId: string,
    reorderData: FieldViewReorder,
    viewTenantId: string = 'GLOBAL'
  ): Promise<FieldView[]> {
    const response = await apiService.api.put<FieldView[]>(
      `${this.baseUrl}/views/${viewId}/fields/reorder`,
      reorderData,
      { params: { view_tenant_id: viewTenantId } }
    );
    return response.data;
  }

  /**
   * Get all views that contain a specific logical field
   * @param fielddefId - Field definition ID
   * @param modelId - Model ID
   */
  async getViewsForField(
    fielddefId: string,
    modelId: string
  ): Promise<
    Array<{
      view_id: string;
      view_tenant_id: string;
      label: string;
      view_type: string;
      model_id: string;
      fv_tenant_id: string;
      display_order: number;
      is_visible: boolean;
      display_subcontainer?: string;
    }>
  > {
    const response = await apiService.api.get<
      Array<{
        view_id: string;
        view_tenant_id: string;
        label: string;
        view_type: string;
        model_id: string;
        fv_tenant_id: string;
        display_order: number;
        is_visible: boolean;
        display_subcontainer?: string;
      }>
    >(`${this.baseUrl}/fields/views`, {
      params: { fielddef_id: fielddefId, model_id: modelId },
    });
    return response.data;
  }

  // =============================================================================
  // SCHEMA GENERATION
  // =============================================================================

  /**
   * Get complete schema for a model
   */
  async getSchema(modelId: string, tenantId: string): Promise<ApiResponse<Schema>> {
    const response = await apiService.api.get<ApiResponse<Schema>>(
      `${this.baseUrl}/schema/${modelId}`,
      { params: { tenant_id: tenantId } }
    );
    return response.data;
  }

  /**
   * Get form configuration for a model
   */
  async getFormConfig(modelId: string, tenantId: string): Promise<ApiResponse<FormConfig>> {
    const response = await apiService.api.get<ApiResponse<FormConfig>>(
      `${this.baseUrl}/schema/${modelId}/form`,
      { params: { tenant_id: tenantId } }
    );
    return response.data;
  }

  // =============================================================================
  // UTILITY METHODS
  // =============================================================================

  /**
   * Validate field definition ID format
   */
  validateFieldDefId(fielddefId: string): { valid: boolean; error?: string } {
    if (!fielddefId) {
      return { valid: false, error: 'Field definition ID is required' };
    }

    if (!fielddefId.startsWith('fd_')) {
      return { valid: false, error: 'Field definition ID must start with "fd_"' };
    }

    if (!/^fd_[a-z0-9-]+$/.test(fielddefId)) {
      return {
        valid: false,
        error: 'Field definition ID must use kebab-case format (fd_example-field)',
      };
    }

    if (fielddefId.length > 50) {
      return { valid: false, error: 'Field definition ID must be under 50 characters' };
    }

    return { valid: true };
  }

  /**
   * Validate model ID format
   */
  validateModelId(modelId: string): { valid: boolean; error?: string } {
    if (!modelId) {
      return { valid: false, error: 'Model ID is required' };
    }

    if (!/^[a-z0-9-]+$/.test(modelId)) {
      return { valid: false, error: 'Model ID must use kebab-case format (example-model)' };
    }

    if (modelId.length > 50) {
      return { valid: false, error: 'Model ID must be under 50 characters' };
    }

    return { valid: true };
  }

  /**
   * Get human readable access level label
   */
  getAccessLevelLabel(access: number | string): string {
    // Convert string to number if needed
    const accessNum = typeof access === 'string' ? parseInt(access, 10) : access;

    switch (accessNum) {
      case 0:
        return 'Internal Only';
      case 1:
        return 'Client Read-Only';
      case 2:
        return 'Client Overrideable';
      case 3:
        return 'Client Overrideable All';
      default:
        return 'Unknown';
    }
  }

  /**
   * Get human readable datatype label
   */
  getDatatypeLabel(datatype: string): string {
    switch (datatype) {
      case 'list':
        return 'List';
      case 'text':
        return 'Text';
      case 'bool':
        return 'Boolean';
      case 'currency':
        return 'Currency';
      case 'decimal':
        return 'Decimal';
      case 'percent':
        return 'Percentage';
      case 'date':
        return 'Date';
      default:
        return datatype;
    }
  }

  /**
   * Get color for status (generic for all grids)
   *
   * Purpose: Centralized color mapping for status chips across all components
   * Handles: active, disabled, deleted, internal_only, and other common statuses
   */
  getStatusColor(status: string): 'success' | 'warning' | 'error' | 'default' | 'info' {
    switch (status) {
      case 'active':
        return 'success';
      case 'disabled':
        return 'warning';
      case 'deleted':
        return 'error';
      case 'internal_only':
        return 'info';
      // Membership statuses (ORCH-410): Active is green, an invite still waiting on the person
      // is amber like `pending`, and a deactivated membership is the neutral gray because it is
      // not an error and shouldn't draw the eye. The theme tints every filled chip, so these
      // names are the whole decision; no grid colours its own chips.
      case 'pending':
      case 'invited':
        return 'warning';
      case 'inactive':
      case 'deactivated':
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  }

  /**
   * Get human-readable label for status (generic for all grids)
   *
   * Purpose: Centralized label formatting for status display
   */
  getStatusLabel(status: string): string {
    switch (status) {
      case 'active':
        return 'Active';
      case 'disabled':
        return 'Disabled';
      case 'deleted':
        return 'Deleted';
      case 'internal_only':
        return 'Internal Only';
      case 'pending':
        return 'Pending';
      case 'invited':
        return 'Invited';
      case 'archived':
        return 'Archived';
      default:
        // Convert snake_case to Title Case as fallback
        return status
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
    }
  }

  /**
   * Get color for access level
   */
  getAccessLevelColor(access: number | string): 'error' | 'warning' | 'info' | 'success' {
    // Convert string to number if needed
    const accessNum = typeof access === 'string' ? parseInt(access, 10) : access;

    switch (accessNum) {
      case 0:
        return 'error'; // Internal Only - red
      case 1:
        return 'warning'; // Read-Only - orange
      case 2:
        return 'info'; // Overrideable - blue
      case 3:
        return 'success'; // Overrideable All - green
      default:
        return 'info';
    }
  }

  // =============================================================================
  // REVERT FUNCTIONALITY
  // =============================================================================

  /**
   * Revert field definition override back to GLOBAL definition
   */
  async revertFieldDefinition(fielddefId: string, tenantId: string): Promise<any> {
    const response = await apiService.api.delete<any>(
      `${this.baseUrl}/field-definitions/${fielddefId}/${tenantId}/revert`
    );
    return response.data;
  }

  /**
   * Revert field override back to GLOBAL field
   */
  async revertField(fieldId: string): Promise<any> {
    const response = await apiService.api.delete<any>(`${this.baseUrl}/fields/${fieldId}/revert`);
    return response.data;
  }

  /**
   * Revert view override back to GLOBAL view
   */
  async revertView(viewId: string, tenantId: string): Promise<any> {
    const response = await apiService.api.delete<any>(
      `${this.baseUrl}/views/${viewId}/${tenantId}/revert`
    );
    return response.data;
  }
}

// Export singleton instance
export const metadataService = new MetadataService();
export default metadataService;
